import { autoPlacement, offset, ReferenceType } from "@floating-ui/react-dom";
import { useClick, useDismiss, useFloating, useInteractions } from "@floating-ui/react-dom-interactions";
import { Alert, Checkbox, Input } from "@material-tailwind/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { useContext, useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { Gallery } from "react-grid-gallery";
import SimpleBar from "simplebar-react";
import { useDebounce } from "use-debounce";
import loadImage from "utils/loadImg";
import resolveImgUrl from "utils/resolveImgUrl";
import { Blog } from "utils/types";
import Spinner from "./utils/Spinner";

type Prop = {
    onSelect : (a : number)=>any | void,
    children : (reference : (node : ReferenceType | null)=>void, referenceProp : Record<string, unknown> )=>JSX.Element,
    strategy? : 'fixed' | 'absolute',
    url : string
}

const fileTypes = ["JPEG", "JPG", "WEBP", "PNG", "GIF"];

const ItemPicker = <T extends {imgUrl : string, title : string, submittedBy : any, id:number},>({
    onSelect,
    children,
    strategy : _strategy = 'absolute',
    url
}:Prop)=>{
  
    const [open, setOpen] = useState(false);
    const [_search, setSearch] = useState('')
    const [search] = useDebounce(_search, 750);
    const [page, setPage] = useState(1);

    const queryClient = useQueryClient();
    const axiosPrivate = useAxiosPrivate();

    const handleSelect = (id : number)=>{
      onSelect(id);
      setSearch('');
      setPage(0);
      setOpen(false);
    }

    const {data : blog, status : blogStatus, refetch : refetchBlog} = useQuery<T[], unknown, T[]>([url.slice(1), page, {search, limit:5, userPosted: false}], async ()=>{
        const {data : _data} = await axiosPrivate.get<[T[], any]>(`${url}?page=${page}&limit=5&q=${search}`);
        console.log(_data);
        const data = _data[0].map((datum)=>{
            const imgUrl = datum.imgUrl.split(".");
            const src = resolveImgUrl( imgUrl[0] + '-thumb.' + imgUrl[1])
            return {
              ...datum, imgUrl : src
            } as T
          })
        return data;
    },)

    useEffect(()=>{
      setPage(1);
      refetchBlog({});
    }, [search])

    const {x, y, reference, floating, strategy, context} = useFloating({
        open,
        onOpenChange: setOpen,
        strategy : _strategy,
        middleware: [offset(10), autoPlacement()]
    });
    const {getReferenceProps, getFloatingProps} = useInteractions([
      useClick(context),
      useDismiss(context),
    ]);

    return <>
        {children(reference, getReferenceProps())}
        {( open && 
          <div
              ref={floating}
              className="z-10"
              style={{
                  position: strategy,
                  top: y ?? 0,
                  left: x ?? 0,
              }}
              {...getFloatingProps()}
          >
            <div className="w-64 text-white flex flex-col">
              <div className="grow border border-slate-400 bg-white shadow-lg relative z-10">
                  <div className="w-full h-full text-slate-900">
                    <div className="mx-2 my-2">
                      <Input label={"Search " + url.slice(1)} value={_search} onChange={(e)=>setSearch(e.target.value)} icon={<i className="fas fa-magnifying-glass"></i>}/>
                    </div>
                    <SimpleBar className="h-[11rem]">
                      <div>
                        {( blogStatus==='loading' && <div className="w-full h-[11rem] grid place-content-center">
                          {blogStatus === "loading" && <Spinner></Spinner>}
                        </div>)}
                        {( blogStatus === 'success' && 
                          blog.length > 0 ?
                            <div className="flex flex-col gap-y-3">
                              {blog.map((datum, index)=><div key={datum.id} onClick={()=>handleSelect(datum.id)} className="relative border-t border-b border-gray-300 group cursor-pointer h-32">
                                <img src={datum.imgUrl} className="object-cover h-full w-full"/>
                                <div className="absolute bottom-0 left-0 w-full bg-black bg-opacity-50 group-hover:bg-opacity-80 flex flex-col justify-center items-center">
                                  <div className="flex flex-col items-center py-3">
                                    <div className="text-white text-sm">{datum.title}</div>
                                    <div className="text-gray-300 text-sm">{datum.submittedBy.name}</div>
                                  </div>
                                </div>
                              </div>)}
                            </div>
                            : <div className="w-full h-[11rem] grid place-content-center">
                              <div className="flex flex-col items-center">
                                <i className="fas fa-circle-question text-4xl"></i>
                                <div>No {url.slice(1)} Found</div>
                              </div>
                            </div>
                        )}
                      </div>
                    </SimpleBar>
                  </div>
              </div>
            </div>
          </div>
        )}
    </>
}

export default ItemPicker;